import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { recipientReminderBundleSchema } from "./shapes";

/**
 * Query Stale Collection Threads
 *
 * Finds every OPEN collection thread in the org that has gone without activity
 * for at least `staleThresholdDays` and is past its reminder gate. Thread
 * assignment is NOT used — the configured Team (`userGroupId`) only defines who
 * gets notified. Emits one bundle per Team member, each carrying the same full
 * list of stale threads, as the `reminders` array. Wire that to a single-object
 * input on the next step to fan out one notification per member (so each can be
 * reached on their own preferred channels).
 *
 * Threads included in the result have their reminder gate pushed forward by
 * `cooldownDays` as part of this step, so the same thread won't be re-reminded
 * until the cooldown elapses (or someone snoozes/acts on it).
 */
export const queryStaleCollectionThreadsStep = defineStepConfig({
  type: "query_stale_collection_threads",
  name: "Query Stale Collection Threads",
  description:
    "Find every open collection thread with no recent activity and fan out " +
    "one reminder per member of the selected Team. Assignment is ignored; " +
    "threads still within their reminder cooldown are skipped.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  // No data inputs — just the implicit flow signal from the schedule trigger.
  inputSchema: z.object({}),

  outputSchema: z.object({
    reminders: z
      .array(recipientReminderBundleSchema)
      .describe(
        "One bundle per Team member, each with the full list of stale " +
          "threads. Connect to a single-object input (e.g. Prepare Collection " +
          "Reminder) to fan out one notification per member.",
      ),
    recipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of Team members who will be notified."),
    threadCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of distinct stale threads in the digest."),
  }),

  configSchema: z.object({
    userGroupId: z
      .string()
      .uuid()
      .describe(
        "The Team whose members are notified. Does not filter which threads " +
          "are considered — every open stale thread in the org is included.",
      ),
    staleThresholdDays: z
      .number()
      .int()
      .positive()
      .default(7)
      .describe(
        "Minimum whole days since the thread's last activity for it to qualify.",
      ),
    cooldownDays: z
      .number()
      .int()
      .positive()
      .default(3)
      .describe(
        "After a thread is included in a reminder, suppress further reminders " +
          "for this many days (the reminder gate). A manual snooze can extend " +
          "it further.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
